import { ArrowRightIcon } from '@phosphor-icons/react'
import * as Tabs from '@radix-ui/react-tabs'
import _ from 'lodash'
import { Link, useSearchParams } from 'react-router'
import { z } from 'zod'
import { pillFromRun, runProgressOf, verifiedFindingsOf } from '../../components/beacon/beacon-run.ts'
import { BeaconScanButton } from '../../components/beacon/beacon-scan-button.tsx'
import { BeaconScanningPill } from '../../components/beacon/beacon-scanning-pill.tsx'
import { ErrorBoundary } from '../../components/error-boundaries/error-boundary'
import { ErrorBoundaryFallback } from '../../components/error-boundaries/error-boundary-fallback.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useOpenGrepRulesContent } from '../../hooks/use-opengrep-rules-content.ts'
import { Routes } from '../../routes.ts'
import { useGetRepositoryLatestBeaconRun } from '../../utils/api-client/api-hooks.ts'
import type { BeaconRun } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { ErrorDetail } from '../../utils/error-handling/error-detail.tsx'
import * as styles from './beacon-tab.css.ts'
import { OpenGrepRulesTab } from './opengrep-rules-tab.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.hooks.tsx'
import { patchSearchParams } from './scans-list.tsx'

export const BeaconTabViewSchema = z.enum(['latest', 'rules'])
export type BeaconTabView = z.infer<typeof BeaconTabViewSchema>

/** Which of the Beacon tab's views is open, in the URL so a link can open the rules directly. */
export const BEACON_TAB_VIEW_PARAM = 'beaconView'

const VIEW_LABELS: Record<BeaconTabView, string> = { latest: 'Latest run', rules: 'Natural language rules' }

const DEFAULT_VIEW: BeaconTabView = 'latest'

/**
 * Pixee Beacon for one repository: a pointer to its latest run, whose page holds the coverage and the
 * replay, and the natural-language rules generated from the threat model.
 */
export const BeaconTab: React.FC<{ onSwitchToThreatModel: () => unknown }> = ({ onSwitchToThreatModel }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const view = BeaconTabViewSchema.safeParse(searchParams.get(BEACON_TAB_VIEW_PARAM)).data ?? DEFAULT_VIEW
  const { content: rules } = useOpenGrepRulesContent({ repositoryId })
  const ruleCount = rules?.rules.length ?? 0

  return (
    <Tabs.Root
      value={view}
      onValueChange={next =>
        setSearchParams(previous =>
          patchSearchParams(previous, {
            [BEACON_TAB_VIEW_PARAM]: BeaconTabViewSchema.parse(next) === DEFAULT_VIEW ? null : next,
          })
        )
      }
    >
      <Tabs.List className={styles.subTabList} aria-label="Beacon views">
        {BeaconTabViewSchema.options.map(option => (
          <Tabs.Trigger key={option} value={option} className={styles.subTabTrigger}>
            {VIEW_LABELS[option]}
            {option === 'rules' && ruleCount > 0 && <span className={styles.subTabCount}>{ruleCount}</span>}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      <Tabs.Content value="latest" tabIndex={-1}>
        <ErrorBoundary fallbackTitle="Unable to load the latest Beacon run">
          <LatestRun repositoryId={repositoryId} />
        </ErrorBoundary>
      </Tabs.Content>
      <Tabs.Content value="rules" tabIndex={-1}>
        <ErrorBoundary fallbackTitle="Unable to load rules">
          <OpenGrepRulesTab onSwitchToThreatModel={onSwitchToThreatModel} />
        </ErrorBoundary>
      </Tabs.Content>
    </Tabs.Root>
  )
}

const formatPercent = (value: number | undefined) => (value === undefined ? '—' : `${Math.round(value)}%`)

/** The repository's latest run at a glance, and the way to its page; with none yet, the way to start one. */
const LatestRun: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const { data: run, isLoading, error, refetch } = useGetRepositoryLatestBeaconRun({ repositoryId })

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading the latest Beacon run" />
      </div>
    )
  }

  // A repository with no run answers `null`; a failure must not read as one, or the tab says there is no
  // run while one is running.
  if (error) {
    return (
      <ErrorBoundaryFallback
        title="Unable to load the latest Beacon run"
        detail={<ErrorDetail error={error} />}
        onRetry={() => refetch()}
      />
    )
  }
  return run ? (
    <LatestRunCard run={run} />
  ) : (
    <div className={styles.empty}>
      <p className={styles.emptyTitle}>No Beacon run yet</p>
      <p className={styles.emptyText}>
        Beacon reads the repository against its threat model and reports what it examined, what it found and what it
        ruled out.
      </p>
      <BeaconScanButton repositoryId={repositoryId} />
    </div>
  )
}

export const LatestRunCard: React.FC<{ run: BeaconRun }> = ({ run }) => {
  const findings = verifiedFindingsOf(run)
  return (
    <section className={styles.card} aria-label="Latest Beacon run">
      <div className={styles.cardHead}>
        <BeaconScanningPill pill={pillFromRun(run)} />
        <Link to={Routes.BeaconRunPage.createPath({ runId: run.id })} className={styles.openLink}>
          Open run
          <ArrowRightIcon aria-hidden size={16} weight="bold" />
        </Link>
      </div>
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Queued</dt>
          <dd>{formatDate({ date: run.queued_at, type: 'mm/dd/yy-time-with-zone' })}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Effort</dt>
          <dd>{_.capitalize(run.effort)}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Verified findings</dt>
          <dd>{findings ?? '—'}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Attack surface examined</dt>
          <dd>{formatPercent(runProgressOf(run)?.examined_percent)}</dd>
        </div>
      </dl>
      <p className={styles.caption}>
        {run.state === 'completed'
          ? 'The run’s page has its coverage, the leads Beacon dismissed and a replay of the run.'
          : 'The run’s page follows it live and keeps what it did once it ends.'}
      </p>
    </section>
  )
}
